import { FC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';

const StyledSelect = styled(Select)`
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const StyledInput = styled(Input)`
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

type JsonSchemaValueProps = {
  id: string;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
} & (
  | {
      schemaType: JsonSchemaTypes.Array;
      value: any;
      onValueChange: (value: any) => void;
    }
  | {
      schemaType: JsonSchemaTypes.Object;
      value: any;
      onValueChange: (value: any) => void;
    }
  | {
      schemaType: JsonSchemaTypes.Boolean;
      value: boolean;
      onValueChange: (value: boolean) => void;
    }
  | {
      schemaType: JsonSchemaTypes.Integer;
      value: number;
      onValueChange: (value: number) => void;
    }
  | {
      schemaType: JsonSchemaTypes.Number;
      value: number;
      onValueChange: (value: number) => void;
    }
  | {
      schemaType: JsonSchemaTypes.String;
      value: string;
      onValueChange: (value: string) => void;
    }
);

export const REACT_SELECT_PORTAL_ID_PREFIX = 'json-schema-value__json-schema-select-';

const JsonSchemaValue: FC<JsonSchemaValueProps> = ({
  id,
  jsonSchema,
  schemaType,
  tokensToDisplayValue,
  value,
  onValueChange,
}) => {
  switch (schemaType) {
    case JsonSchemaTypes.Array:
    case JsonSchemaTypes.Object:
      return (
        <StyledInput
          aria-label={`Select ${schemaType} value`}
          disabled
          placeholder='Not supported'
        />
      );
    case JsonSchemaTypes.Boolean:
      return (
        <StyledSelect
          value={value ? 'true' : 'false'}
          triggerProps={{ 'aria-label': 'Select boolean value' }}
          onValueChange={(value) => onValueChange(value === 'true')}
        >
          <Option value='true'>True</Option>
          <Option value='false'>False</Option>
        </StyledSelect>
      );
    case JsonSchemaTypes.Integer:
    case JsonSchemaTypes.Number:
      return (
        <StyledInput
          aria-label='Enter number value'
          type='number'
          value={String(value)}
          onChange={(e) => onValueChange(parseInt(e.target.value, 10))}
        />
      );
    case JsonSchemaTypes.String:
      return (
        <HandlebarFormControl
          aria-label='Enter string value'
          size='md'
          type='input'
          value={value}
          updateValue={onValueChange}
          editable
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          placeholder='Enter value'
          id={`${REACT_SELECT_PORTAL_ID_PREFIX}${id}`}
          style={{ borderLeft: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        />
      );
    default:
      assertNever(schemaType);
  }
};

export default JsonSchemaValue;
